import React, { useState, useEffect, useContext } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
import { getMilmaAdmins, createMilmaAdmin, updateMilmaAdmin, deleteMilmaAdmin } from '../../../api/milma_admin/MilmaAdminUserAPI';
import { risapi, getAxiosConfig } from '../../../api/risapi';
import { AuthContext } from '../../AuthContext';

const MilmaAdminsPage = () => {
    const [admins, setAdmins] = useState({ data: [], current_page: 1, tot_page: null });
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);

    const { user: currentUser } = useContext(AuthContext);

    // Form Modal State
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ id: null, name: '', phone_number: '', password: '', password_confirmation: '', regions: [] });
    const [formErrors, setFormErrors] = useState({});
    const [saving, setSaving] = useState(false);
    
    // Master Data
    const [regionOptions, setRegionOptions] = useState([]);

    useEffect(() => {
        // Fetch Region Master for multi-select
        risapi.get('/api/milma-admin/accessible-regions', getAxiosConfig()).then(res => {
            if(res.data.regions) setRegionOptions(res.data.regions.map(r => ({ value: r.id, label: r.name })));
        });
    }, []);

    useEffect(() => {
        setLoading(true);
        getMilmaAdmins({ paginate: true, page: currentPage, per_page: perPage, search_term: searchTerm }, (res) => {
            setLoading(false);
            if (res.admins) setAdmins(res.admins);
        });
    }, [currentPage, perPage, searchTerm, refresh]);

    const handleSearch = (e) => { e.preventDefault(); setCurrentPage(1); setRefresh(!refresh); };

    const openModal = (admin = null) => {
        setIsEditing(!!admin);
        setFormErrors({});
        if (admin) {
            setFormData({
                id: admin.id, name: admin.name, phone_number: admin.phone_number, password: '', password_confirmation: '',
                regions: admin.accessible_regions.map(r => r.id)
            });
        } else {
            setFormData({ id: null, name: '', phone_number: '', password: '', password_confirmation: '', regions: [] });
        }
        setShowModal(true);
    };

    const handleSave = (e) => {
        e.preventDefault();
        setSaving(true);
        setFormErrors({});

        const action = isEditing ? updateMilmaAdmin(formData.id, formData, handleResponse) : createMilmaAdmin(formData, handleResponse);
        
        function handleResponse(res) {
            setSaving(false);
            if (res.error) {
                if (res.error?.response?.status === 422) setFormErrors(res.error.response.data.errors);
                else Swal.fire('Error', 'Something went wrong', 'error');
            } else {
                Swal.fire('Success', `Milma Admin ${isEditing ? 'updated' : 'created'}`, 'success');
                setShowModal(false);
                setRefresh(!refresh);
            }
        }
    };

    const handleDelete = (id) => {
        Swal.fire({ title: 'Are you sure?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes, delete it!' })
        .then((res) => {
            if (res.isConfirmed) {
                deleteMilmaAdmin(id, (delRes) => {
                    if (delRes.error) Swal.fire('Error', 'Could not delete', 'error');
                    else { Swal.fire('Deleted!', '', 'success'); setRefresh(!refresh); }
                });
            }
        });
    };

    return (
        <>
            <AppBreadCrumb entityName="Milma Admins" breadCrumbType="list" />
            <main className="page-content">
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5>Milma Admins (Global Access Only)</h5>
                            <button className="btn btn-primary btn-sm" onClick={() => openModal()}>
                                <i className="ti ti-plus"></i> Add New Admin
                            </button>
                        </div>
                        <div className="card-body">
                            {/* Toolbar */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <PerPageBlock perPage={perPage} setPerPage={setPerPage} setCurrentPage={setCurrentPage} />
                                <AppSearchBox searchTerm={searchTerm} handleSearchChange={(e) => setSearchTerm(e.target.value)} handleSearchSubmit={handleSearch} handleClearSearch={() => {setSearchTerm(''); setRefresh(!refresh);}} />
                            </div>
                            
                            {/* Table */}
                            <div className="table-responsive">
                                <table className="table table-hover table-bordered mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Name</th>
                                            <th>Phone Number</th>
                                            <th>Accessible Regions</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {admins.data.map(admin => (
                                            <tr key={admin.id}>
                                                <td><strong>{admin.name}</strong></td>
                                                <td>{admin.phone_number}</td>
                                                <td>
                                                    {admin.accessible_regions?.map(r => (
                                                        <span key={r.id} className="badge bg-light-primary text-primary me-1">{r.name}</span>
                                                    ))}
                                                </td>
                                                <td className="text-end">
                                                    <button className="btn btn-sm btn-outline-primary px-2 py-1 me-2" onClick={() => openModal(admin)}><i className="ti ti-edit"></i></button>
                                                   {/* --- Hide delete button if it's the logged in user --- */}
                                                    {currentUser?.id !== admin.id && (
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger px-2 py-1" 
                                                            onClick={() => handleDelete(admin.id)}
                                                        >
                                                            <i className="ti ti-trash"></i>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {loading && <tr><td colSpan="4" className="text-center">Loading...</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                            <AppPaginator entityName="Admins" currentPage={admins.current_page} totalPages={admins.tot_page} setCurrentPage={setCurrentPage} />
                        </div>
                    </div>
                </div>
            </main>

            {/* Form Modal */}
            {showModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show d-block" tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <form className="modal-content" onSubmit={handleSave}>
                                <div className="modal-header">
                                    <h5 className="modal-title">{isEditing ? 'Edit Admin' : 'New Milma Admin'}</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Name *</label>
                                        <input className={`form-control ${formErrors.name ? 'is-invalid' : ''}`} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                                        {formErrors.name && <span className="text-danger small">{formErrors.name[0]}</span>}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Phone Number *</label>
                                        <input className={`form-control ${formErrors.phone_number ? 'is-invalid' : ''}`} value={formData.phone_number} onChange={e => setFormData({...formData, phone_number: e.target.value})} />
                                        {formErrors.phone_number && <span className="text-danger small">{formErrors.phone_number[0]}</span>}
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label">Assign Regions *</label>
                                        <Select 
                                            isMulti
                                            options={regionOptions}
                                            value={regionOptions.filter(opt => formData.regions.includes(opt.value))}
                                            onChange={(selected) => setFormData({...formData, regions: selected.map(s => s.value)})}
                                        />
                                        {formErrors.regions && <span className="text-danger small">{formErrors.regions[0]}</span>}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Password {isEditing && '(Leave blank to keep current)'}</label>
                                        <input type="password" className={`form-control ${formErrors.password ? 'is-invalid' : ''}`} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                                        {formErrors.password && <span className="text-danger small">{formErrors.password[0]}</span>}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Confirm Password</label>
                                        <input type="password" className="form-control" value={formData.password_confirmation} onChange={e => setFormData({...formData, password_confirmation: e.target.value})} />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Admin'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default MilmaAdminsPage;